import { CodeNameModel } from 'components/common/types';
import {
  DirectSalesFilter,
  DirectSalesFilterGroupCriteria,
  DirectSalesObjectiveTarget,
  DirectSalesRequestFormatCriteria,
  DirectSalesTargetCriteria,
  DirectSalesTargetTolerancePriority,
} from 'components/common/types/DirectSalesCampaign.types';
import { DirectSalesTargetTolerance } from 'components/common/types/DirectSalesTargetTolerance.types';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';

type TransformedFilterGroupCriteria = {
  [filterGroupCriteria in DirectSalesFilterGroupCriteria]?: DirectSalesTargetTolerance & {
    objectiveId: number | null;
    priority: CodeNameModel;
    target: number;
  };
};

export const transformTargetCriteriaValue = (
  target: number,
  { greenTolerance, orangeTolerance, priority }: DirectSalesTargetTolerancePriority,
): DirectSalesRequestFormatCriteria => {
  return {
    target,
    greenTolerance: greenTolerance / 100,
    orangeTolerance: orangeTolerance / 100,
    priority: +priority.code,
  };
};

export const transformDirectSalesObjectivesTarget = ({
  name,
  value,
  objectiveId,
  greenTolerance,
  orangeTolerance,
  priority: { code },
  objectiveGroupId,
  criteriaType,
  filter,
}: DirectSalesObjectiveTarget & {
  name: string;
  objectiveGroupId: number;
  criteriaType: DirectSalesFilterGroupCriteria;
  filter: DirectSalesFilter;
}): DirectSalesTargetCriteria => ({
  name,
  criteriaType,
  value: {
    target: Number(value),
    greenTolerance: greenTolerance / 100,
    orangeTolerance: orangeTolerance / 100,
    priority: Number(code),
  },
  objectiveId: Number(objectiveId) || null,
  objectiveGroupId,
  filter,
});

export const transformTargetCriteria = (
  targetCriteria: DirectSalesTargetCriteria[],
): TransformedFilterGroupCriteria => {
  const criteria: TransformedFilterGroupCriteria = {};

  targetCriteria?.forEach((criterion) => {
    criteria[criterion.criteriaType] = {
      objectiveId: criterion.objectiveId,
      target:
        criterion.criteriaType === DirectSalesFilterGroupCriteria.SOT_TARGET
          ? criterion.value.target * 100
          : criterion.value.target,
      greenTolerance: criterion.value.greenTolerance * 100,
      orangeTolerance: criterion.value.orangeTolerance * 100,
      priority: {
        code: criterion.value.priority?.toString() ?? '',
        name: criterion.value.priority?.toString() ?? '',
      },
    };
  });

  return criteria;
};

export const transformNetworkCriteria = (targetCriteria: DirectSalesTargetCriteria[] = []): NetworkCriteriaType[] => {
  if (!targetCriteria.length) return [];

  const networkCriteria = targetCriteria.filter(
    (criteria) => criteria.criteriaType === DirectSalesFilterGroupCriteria.NETWORK_TARGET,
  );

  return networkCriteria.map((criteria) => {
    return {
      code: criteria.value.networkId ?? '',
      objectiveId: criteria.objectiveId,
      name: criteria.value.networkName ?? '',
      category: '',
      target: criteria.value.target.toString(),
      greenTolerance: criteria.value.greenTolerance * 100,
      orangeTolerance: criteria.value.orangeTolerance * 100,
      priority: {
        code: criteria.value.priority?.toString() || '',
        name: criteria.value.priority?.toString() || '',
      },
      include: true,
    };
  });
};

export const transformObjectiveTarget = ({
  objectiveId,
  value: { priority, target, greenTolerance, orangeTolerance },
}: DirectSalesTargetCriteria): DirectSalesObjectiveTarget => {
  return {
    value: target,
    greenTolerance: greenTolerance * 100,
    orangeTolerance: orangeTolerance * 100,
    objectiveId: objectiveId ?? undefined,
    priority: { code: `${priority}`, name: `${priority}` },
  };
};
